import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, MapPinIcon, ArrowRightIcon, SparklesIcon } from 'lucide-react';
const contactCards = [{
  name: 'Alexander Graham',
  email: 'alex33@contact.com'
}, {
  name: 'Alena Levin',
  email: 'alena@levin.co'
}, {
  name: 'Angel George',
  email: 'angel@georgelaw.com'
}, {
  name: 'Jaydon Saris',
  email: 'saris@contact.com'
}, {
  name: 'Zain Lubin',
  email: 'zain@lubinco.com'
}, {
  name: 'Marcus Chen',
  email: 'marcus@growthco.com'
}, {
  name: 'Sarah Jenkins',
  email: 'sarah@techflow.io'
}, {
  name: 'Elena Rodriguez',
  email: 'elena@rodlaw.com'
}];
const businessTypes = ['restaurants', 'HVAC companies', 'dental clinics', 'law firms', 'nail salons', 'plumbers', 'real estate agents', 'auto repair shops'];
const cities = ['Austin, TX', 'New York, NY', 'San Francisco, CA', 'Miami, FL', 'Chicago, IL', 'Los Angeles, CA', 'Seattle, WA', 'Denver, CO'];
const popularCategories = ['Restaurants', 'Plumbers', 'Dentists', 'HVAC', 'Electricians', 'Law Firms', 'Real Estate', 'Auto Repair', 'Hair Salons', 'Accountants'];
function ContactCard({
  name,
  email
}: {
  name: string;
  email: string;
}) {
  const initials = name.split(' ').map(n => n[0]).join('');
  const colors = ['bg-blue-400', 'bg-blue-300', 'bg-sky-400', 'bg-indigo-400', 'bg-cyan-400'];
  const color = colors[name.length % colors.length];
  return <div className="flex items-center gap-3 bg-blue-500/40 backdrop-blur-sm rounded-xl px-4 py-3 min-w-[280px] shrink-0" data-id="element-2770">
      <div className={`w-10 h-10 rounded-full ${color} flex items-center justify-center text-white font-bold text-sm shrink-0`} data-id="element-2771">
        {initials}
      </div>
      <div className="min-w-0" data-id="element-2772">
        <div className="text-white font-bold text-sm truncate" data-id="element-2773">{name}</div>
        <div className="text-blue-100 text-xs" data-id="element-2774">Local business owner</div>
        <div className="text-blue-200 text-xs truncate" data-id="element-2775">{email}</div>
      </div>
      <span className="ml-auto bg-white/20 text-white text-[10px] font-bold px-2 py-0.5 rounded shrink-0" data-id="element-2776">
        Lead
      </span>
    </div>;
}
function AnimatedPlaceholder({
  items,
  prefix
}: {
  items: string[];
  prefix: string;
}) {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setIndex(prev => (prev + 1) % items.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [items.length]);
  return <span className="text-slate-300 pointer-events-none select-none" data-id="element-2777">
      {prefix}
      <AnimatePresence mode="wait" data-id="element-2778">
        <motion.span key={index} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.25
      }} className="inline-block" data-id="element-2779">
          {items[index]}
        </motion.span>
      </AnimatePresence>
    </span>;
}
export function HeroPreview() {
  const row1 = [...contactCards.slice(0, 4), ...contactCards.slice(0, 4), ...contactCards.slice(0, 4)];
  const row2 = [...contactCards.slice(4), ...contactCards.slice(4), ...contactCards.slice(4)];
  return <div className="mt-12" data-id="element-2780">
      {/* Scrolling Contact Card Ticker */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.8,
      delay: 0.9
    }} className="relative bg-blue-600 rounded-b-[2.5rem] shadow-xl pt-8 pb-12 overflow-hidden border-t border-blue-400/30" data-id="element-2781">
        <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-blue-600 to-transparent z-10 pointer-events-none" data-id="element-2782" />
        <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-blue-600 to-transparent z-10 pointer-events-none" data-id="element-2783" />

        <div className="flex w-max animate-ticker mb-4" data-id="element-2784">
          {row1.map((card, i) => <div key={i} className="px-2" data-id="element-2785">
              <ContactCard name={card.name} email={card.email} data-id="element-2786" />
            </div>)}
        </div>

        <div className="flex w-max animate-ticker-reverse" data-id="element-2787">
          {row2.map((card, i) => <div key={i} className="px-2" data-id="element-2788">
              <ContactCard name={card.name} email={card.email} data-id="element-2789" />
            </div>)}
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 40
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        delay: 1.2,
        type: 'spring'
      }} className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[70%] max-w-2xl" data-id="element-2790">
          <div className="bg-white rounded-t-xl shadow-2xl border border-slate-200 overflow-hidden" data-id="element-2791">
            <div className="bg-slate-50 border-b border-slate-100 px-4 py-2 flex items-center gap-2" data-id="element-2792">
              <div className="flex gap-1.5" data-id="element-2793">
                <div className="w-2.5 h-2.5 rounded-full bg-red-400" data-id="element-2794" />
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" data-id="element-2795" />
                <div className="w-2.5 h-2.5 rounded-full bg-green-400" data-id="element-2796" />
              </div>
              <div className="text-[10px] text-slate-400 font-mono ml-2" data-id="element-2797">
                app.openmart.com
              </div>
            </div>
            <div className="p-4 space-y-3" data-id="element-2798">
              <div className="flex gap-3" data-id="element-2799">
                <div className="w-1/3 bg-slate-50 rounded-lg p-3 space-y-2" data-id="element-2800">
                  <div className="h-2 w-16 bg-slate-200 rounded" data-id="element-2801" />
                  <div className="h-2 w-12 bg-slate-100 rounded" data-id="element-2802" />
                  <div className="h-2 w-20 bg-slate-100 rounded" data-id="element-2803" />
                  <div className="h-2 w-14 bg-slate-100 rounded" data-id="element-2804" />
                </div>
                <div className="flex-1 space-y-2" data-id="element-2805">
                  <div className="flex gap-2 items-center" data-id="element-2806">
                    <div className="h-3 w-24 bg-slate-200 rounded" data-id="element-2807" />
                    <div className="h-6 w-6 bg-blue-100 rounded" data-id="element-2808" />
                  </div>
                  <div className="bg-green-50 rounded-lg h-24 flex items-center justify-center" data-id="element-2809">
                    <div className="w-3/4 h-16 bg-green-100/50 rounded" data-id="element-2810" />
                  </div>
                  <div className="grid grid-cols-3 gap-2" data-id="element-2811">
                    <div className="h-12 bg-slate-50 rounded-lg" data-id="element-2812" />
                    <div className="h-12 bg-slate-50 rounded-lg" data-id="element-2813" />
                    <div className="h-12 bg-slate-50 rounded-lg" data-id="element-2814" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>;
}